import {
  Alert,
  Box,
  Divider,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Tabs,
  Text,
} from '@mantine/core';
import { useSupportCasesControllerGetSupportCasesForWorkOrder } from '@/generated/api/support-cases.ts';
import type { SupportCaseDto } from '@/generated/api/pacApi.schemas.ts';
import { useMemo } from 'react';
import { DateTime } from 'luxon';
import AnchorButton from '@/common/components/AnchorButton.tsx';
import useSidebarStack from '@/common/sidebar/useSidebarStack.ts';
import { SupportCaseStatusBadge } from '@/features/support-cases/components/SupportCaseStatusBadge.tsx';
import { SupportCaseDescription } from '@/features/support-cases/components/SupportCaseDescription.tsx';
import { WORK_ORDER_AUTO_REFRESH_QUERY_OPTIONS } from '../../autoRefresh.ts';

export default function SupportCasesTabBody({
  workOrderId,
}: {
  workOrderId: string;
}) {
  const supportCasesQuery =
    useSupportCasesControllerGetSupportCasesForWorkOrder(workOrderId, {
      query: WORK_ORDER_AUTO_REFRESH_QUERY_OPTIONS,
    });
  const supportCases = useMemo(
    () => supportCasesQuery.data?.supportCases ?? [],
    [supportCasesQuery.data],
  );

  return (
    <Tabs.Panel
      value="support-cases"
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <ScrollArea style={{ height: '100%' }}>
        {supportCasesQuery.isLoading ? (
          <Loader />
        ) : (
          <>
            {supportCases.length > 0 ? (
              <Paper withBorder radius="md">
                {supportCases.map((supportCase, index) => (
                  <>
                    <SupportCaseCard supportCase={supportCase} />
                    {index < supportCases.length - 1 && <Divider />}
                  </>
                ))}
              </Paper>
            ) : (
              <Alert color="gray" title="No support cases yet">
                This work order does not have any support cases.
              </Alert>
            )}
          </>
        )}
      </ScrollArea>
    </Tabs.Panel>
  );
}

function SupportCaseCard({ supportCase }: { supportCase: SupportCaseDto }) {
  const sidebarStack = useSidebarStack();

  const openedBy =
    supportCase.createdBy &&
    supportCase.createdBy.firstName &&
    supportCase.createdBy.lastName
      ? `Opened by ${supportCase.createdBy?.firstName} ${supportCase.createdBy?.lastName} • `
      : 'Opened ';
  const openedAtRelative = DateTime.fromISO(supportCase.openedAt).toRelative();
  const headerLeft = `${openedBy}${openedAtRelative ? `${openedAtRelative}` : ''}`;
  const lastMessageRelative = supportCase.lastMessageAt
    ? DateTime.fromISO(supportCase.lastMessageAt).toRelative()
    : '';
  const headerRight = lastMessageRelative
    ? `Last message • ${lastMessageRelative}`
    : '';

  return (
    <Box key={supportCase.id}>
      <Stack gap={8} px="md" py="sm">
        <Group gap="xs" justify="space-between">
          {supportCase.caseId ? (
            <AnchorButton
              onClick={() =>
                sidebarStack.pushItem('supportCaseDetails', {
                  supportCaseId: supportCase.id,
                  supportCaseExternalId: supportCase.caseId,
                })
              }
            >
              {`Case #${supportCase.caseId}`}
            </AnchorButton>
          ) : (
            <Text size="sm" fw={400} c="dimmed">
              Unknown Case #
            </Text>
          )}

          <SupportCaseStatusBadge status={supportCase.status} />
        </Group>
        <Group gap="xs" justify="space-between">
          <Text size="xs">{headerLeft}</Text>
          <Text size="xs">{headerRight}</Text>
        </Group>
        <Divider />
        <SupportCaseDescription
          description={supportCase.description
            .replace(
              /\n?---- Work order information ----[\s\S]*?---- End work order information ----\n*/g,

              '\n',
            )
            .trim()}
        />
      </Stack>
    </Box>
  );
}
